import { NgClass } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ApizUIDataField } from '@compiere-ws/models/apiz-domains';
import {
  CompiereDataGridType,
  DataStore,
  DataStoreName,
  DataStoreRequest,
} from '@compiere-ws/models/compiere-data-json';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import { DataStoreService } from '@iupics-manager/managers/data-store/data-store.service';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { DataConflict } from '@iupics-manager/models/data-conflict';
import { SubscriptionsUtils } from '@iupics-util/tools/subscriptions.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';
import { ButtonModule } from 'primeng/button';
import { Subscription } from 'rxjs';

@Component({
  selector: 'iu-mergetool',
  styleUrls: ['./mergetool.component.scss'],
  templateUrl: './mergetool.component.html',
  standalone: true,
  imports: [NgClass, ButtonModule, TranslateModule],
})
export default class MergetoolComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  private translate = inject(TranslateService);
  @Input()
  parentEditView: EditViewUiComponent;

  @Input()
  conflictsResult: DataConflict;

  localData: DataStore;

  @Output()
  mergeEmitter = new EventEmitter<any>();
  /** Close without merging: the conflict stays pending */
  @Output()
  cancelEmitter = new EventEmitter<void>();

  dataToMerge = { fields: [], local: {}, remote: {} };
  mergedData = new DataStore();
  mergedInfo = {};
  colNameToLabel = new Map<string, string>();
  /* field metadata by columnName, used to format values */
  colNameToField = new Map<string, ApizUIDataField>();
  isSelected = {};
  /* who/when modified the server version, when the remote store has it */
  remoteUpdatedBy: string = null;
  remoteUpdated: string = null;

  constructor(
    private store: DataStoreService,
    private uiCreator: UICreatorService
  ) {}

  ngOnInit() {
    const currentDataStoreKey = this.parentEditView.currentDataStoreKey;
    const request: DataStoreRequest = {
      windowId: currentDataStoreKey.windowId,
      record_id: currentDataStoreKey.recordId,
      parent_constraint: currentDataStoreKey.parentId,
      compiereRequest: {
        windowType: CompiereDataGridType.WINDOW,
        entityId: currentDataStoreKey.tabId,
        startRow: 0,
        endRow: 1,
        windowCtx: this.parentEditView.getCurrentContext(),
        validation: this.parentEditView.getTabWhereclause(),
      },
    };
    this.store.getWindowSingleData(request).subscribe((dataStore) => {
      this.localData = dataStore;
    });
    this.checkDataToMerge();
    this.getFieldsToDisplay();
    this.initRemoteInfo();
  }

  /**
   * Check the data to merge to prepare the mergetool
   */
  checkDataToMerge() {
    const fieldNames = Object.keys(this.conflictsResult.dataConflict);
    for (const fieldName of fieldNames) {
      this.dataToMerge.fields.push(fieldName);
    }
  }

  /**
   * Get the name of the different fields to display
   */
  getFieldsToDisplay() {
    // tabId from the edit view key: localData may still be loading here
    const tabId = this.localData?.key?.tabId ?? this.parentEditView.currentDataStoreKey.tabId;
    this.subscriptions.push(
      this.uiCreator.getTab(this.parentEditView.currentDataStoreKey.windowId, tabId).subscribe((result) => {
        this.collectFields(result?.editTabs?.[0]?.children ?? []);
      })
    );
  }

  /**
   * Walk the tab tree (any depth) and keep the metadata of the conflicting columns.
   * isApizUIField() was too strict: null props (formId, processId...) are not serialized.
   */
  private collectFields(items: any[]) {
    for (const item of items) {
      const data: ApizUIDataField = item?.data;
      if (data?.columnName && this.dataToMerge.fields.includes(data.columnName)) {
        if (!this.colNameToField.has(data.columnName)) {
          this.colNameToField.set(data.columnName, data);
          this.colNameToLabel.set(data.columnName, data.label);
        }
      }
      if (Array.isArray(item?.children) && item.children.length) {
        this.collectFields(item.children);
      }
    }
  }

  private initRemoteInfo() {
    try {
      const remote = this.store.getStore(this.parentEditView.currentDataStoreKey, DataStoreName.REMOTE) as DataStore;
      const updatedBy = remote?.data?.['UpdatedBy'];
      if (updatedBy instanceof Object) {
        this.remoteUpdatedBy = updatedBy['displayValue'] ?? null;
      } else if (typeof updatedBy === 'string' && updatedBy.trim()) {
        this.remoteUpdatedBy = updatedBy;
      }
      const updated = remote?.data?.['Updated'];
      if (updated !== undefined && updated !== null && updated !== '') {
        this.remoteUpdated = this.formatDate(updated, true);
      }
    } catch {
      // informative only
    }
  }

  /**
   *
   * @param event MouseEvent
   * @param field The chosen field to merge
   * @param data The data to merge
   * @param who From who the data come
   */
  onClick(event: MouseEvent, field: string, data: any, who: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.mergedData.data[field] = cloneDeep(data);
    this.isSelected[field] = { who: who };
  }

  /**
   * Keep all the data from one side, local or remote
   * @param event MouseEvent
   * @param who From who the data must be kept
   */
  keepAll(event: MouseEvent, who: string) {
    event.stopPropagation();
    event.preventDefault();
    for (const field of this.dataToMerge.fields) {
      this.onClick(
        document.createEvent('MouseEvent'),
        field,
        who === 'local' ? this.localData.data[field] : this.conflictsResult.dataConflict[field],
        who
      );
    }
  }

  /**
   * Save and emit the merged data
   * @param event MouseEvent
   */
  save(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    for (const field of Object.keys(this.localData.data)) {
      // a side picked explicitly is kept even when empty / 0 / false
      if (!this.isSelected[field] && !this.mergedData.data[field]) {
        this.mergedData.data[field] = cloneDeep(this.localData.data[field]);
      }
    }
    const dataToSend = {};
    for (const field of this.dataToMerge.fields) {
      dataToSend[field] = this.mergedData.data[field];
    }
    this.mergeEmitter.emit({ dataMerged: dataToSend, mergedInfo: this.isSelected });
  }

  ngOnDestroy() {
    SubscriptionsUtils.unsubscribe(...this.subscriptions);
  }

  getDisplayValue(data: any) {
    return data instanceof Object ? data.displayValue : data;
  }

  /* --- display helpers (no effect on merged data) --- */

  getLabel(col: string): string {
    return this.colNameToLabel.get(col) || col;
  }

  getLocalValue(col: string): any {
    return this.localData?.data?.[col];
  }

  getRemoteValue(col: string): any {
    return this.conflictsResult.dataConflict[col];
  }

  getSelectedSide(col: string): string {
    return this.isSelected[col]?.who ?? null;
  }

  /* value save() will send: the picked side, else the local one */
  getMergedValue(col: string): any {
    return this.isSelected[col] ? this.mergedData.data[col] : this.getLocalValue(col);
  }

  isMergedFallback(col: string): boolean {
    return !this.isSelected[col];
  }

  cancel(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.cancelEmitter.emit();
  }

  get selectedCount(): number {
    return this.dataToMerge.fields.filter((f) => !!this.isSelected[f]).length;
  }

  pick(event: MouseEvent, col: string, who: 'local' | 'remote') {
    if (who === 'local' && !this.localData) {
      return;
    }
    this.onClick(event, col, who === 'local' ? this.getLocalValue(col) : this.getRemoteValue(col), who);
  }

  /**
   * Readable value: lookup display value, Oui/Non, formatted date; null when empty
   */
  formatValue(col: string, value: any): string {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    if (value instanceof Object) {
      const display = value['displayValue'];
      return display === undefined || display === null || display === '' ? null : String(display);
    }
    const component = this.colNameToField.get(col)?.componentName;
    if (component === 'InputSwitchUiComponent' || typeof value === 'boolean') {
      const yes = value === true || String(value).toUpperCase() === 'Y';
      return this.translate.instant(yes ? 'generic.yes' : 'generic.no');
    }
    if (component === 'CalendarUiComponent') {
      return this.formatDate(value, !!this.colNameToField.get(col)?.needTime);
    }
    return String(value);
  }

  private formatDate(value: any, withTime: boolean): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return String(value);
    }
    try {
      const lang = (this.translate.currentLang || '').replace('_', '-') || undefined;
      const options: Intl.DateTimeFormatOptions = withTime
        ? { dateStyle: 'short', timeStyle: 'short' }
        : { dateStyle: 'short' };
      return new Intl.DateTimeFormat(lang, options).format(date);
    } catch {
      return date.toLocaleString();
    }
  }
}
